import { useEffect, useState } from "react";
import axios from "axios";
import { BookOpen } from "lucide-react";
import { SeoHead } from "@/lib/seo";
import { Breadcrumb, breadcrumbJsonLd } from "@/components/Breadcrumb";
import BlogCard from "@/components/BlogCard";
import { useLang } from "@/lib/lang-context";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const { lang } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/blog/posts`, {
          params: { limit: 30, include_scheduled: true, lang },
        });
        if (!cancelled) setPosts(data?.items || []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[blog] fetch failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const published = posts.filter((p) => p.status === "published");
  const scheduled = posts.filter((p) => p.status === "scheduled");

  const seoTitle =
    lang === "it"
      ? "Blog · Mercato immobiliare Salento · Deal Rating"
      : "Blog · Salento real-estate insights · Deal Rating";
  const seoDesc =
    lang === "it"
      ? "Guide pratiche, dati e curiosità sul mercato immobiliare del Salento. Trulli, lamie, vincoli, IMU, rendita: tutto quello che serve sapere prima di comprare."
      : "Practical guides, data and curiosities on the Salento real-estate market. Trulli, masserie, planning constraints, rental yield: everything to know before you buy.";

  return (
    <main className="bg-[#FBFAF7] min-h-screen">
      <SeoHead
        title={seoTitle}
        description={seoDesc}
        canonical="/blog"
        jsonLd={[
          breadcrumbJsonLd([
            { label: "Home", to: "/" },
            { label: "Blog", to: "/blog" },
          ]),
        ]}
      />

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <Breadcrumb
          items={[
            { label: lang === "it" ? "Home" : "Home", to: "/" },
            { label: "Blog", to: "/blog" },
          ]}
        />

        <div className="mt-6 mb-10">
          <div className="label-eyebrow inline-flex items-center gap-2">
            <BookOpen size={11} strokeWidth={1.75} />
            {lang === "it" ? "Rivista" : "Magazine"}
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#282C2A] leading-tight mt-3">
            {lang === "it"
              ? "Guide, dati e curiosità sull'immobiliare del Salento."
              : "Guides, data and curiosities about Salento real-estate."}
          </h1>
          <p className="mt-4 text-base text-[#6C706D] max-w-3xl leading-relaxed">
            {lang === "it"
              ? "Un articolo al mese, scritto dalla redazione di Deal Rating. Trulli, lamie, vincoli, fiscalità, rendite: tutto quello che dovresti sapere prima di mettere la firma."
              : "One article a month from the Deal Rating editorial team. Trulli, lamie, planning constraints, taxation, yield: everything to know before you sign."}
          </p>
        </div>

        {/* Published */}
        <section>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {published.map((p) => (
              <BlogCard key={p.slug} post={p} lang={lang} />
            ))}
          </div>
        </section>

        {/* Scheduled / Calendario editoriale */}
        {scheduled.length > 0 && (
          <section className="mt-16">
            <div className="border-t border-[#E6E4DD] pt-8">
              <div className="label-eyebrow text-[#A89968] mb-2">
                {lang === "it" ? "Calendario editoriale" : "Editorial calendar"}
              </div>
              <h2 className="font-serif-display text-3xl text-[#282C2A] leading-tight">
                {lang === "it" ? "Prossimi articoli" : "Upcoming articles"}
              </h2>
              <p className="mt-2 text-sm text-[#6C706D] max-w-2xl">
                {lang === "it"
                  ? "Un nuovo approfondimento ogni mese — torna a leggere alla data indicata."
                  : "A new deep-dive every month — come back on the date below."}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {scheduled.map((p) => (
                <BlogCard key={p.slug} post={p} lang={lang} />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default Blog;
